// Components
import { Select, Skeleton, Textarea } from "@mantine/core";
import { FormSection } from "@benchmark/ui/components/atomic";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormGroup } from "@benchmark/ui/components/atomic";
import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import { PatientEngagementStatusSection } from "@/components/shared/forms/PatientEngagementStatusSection";
import { FollowUpFlagsSection } from "@/components/shared/forms/FollowUpFlagsSection";
import { FollowUpTaskSection } from "@/components/shared/forms/FollowUpTaskSection";

// Constants
import {
  COMMUNICATION_MEDIUM_SELECT_OPTIONS,
  PATIENT_TAG_MAPPINGS,
} from "@benchmark/core/constants";
import { PRACTITIONER_ROLE_PAGINATION_COUNT } from "@benchmark/core/constants";
import { CONFIG } from "@/app/config";

// Hooks
import { useForm } from "@mantine/form";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import {
  useCallback,
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { useNavigate } from "react-router";
import { useFormSubmission } from "@benchmark/ui/hooks";
import { useStableParams } from "@/hooks/common/useStableParams";
import { useFormProtection } from "@benchmark/ui/hooks";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { usePractitionerRoleOptions } from "@/hooks/shared/useFHIROptions";
import { usePatientFlags } from "@/hooks/domain/usePatientFlags";

// Types
import type {
  Communication,
  Practitioner,
  Patient,
  PractitionerRole,
} from "@medplum/fhirtypes";
import type { CommunicationFormData } from "@benchmark/core/types";

// Utils
import { communicationToForm } from "@benchmark/core/adapters";
import {
  formatDateTimeForInput,
  formatInputForDateTime,
} from "@benchmark/ui/utils";
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
} from "@benchmark/ui/utils";
import { createCommunicationFacade } from "@/facades/communication.facade";

// Interfaces

/**
 * Ref interface for CommunicationForm exposing form protection check.
 * @agentic connections
 */
export type CommunicationFormRef = {
  checkProtection: () => Promise<boolean>;
};

/**
 * Props for the CommunicationForm component.
 * @agentic connections
 */
export type CommunicationFormProps = {
  patientId?: string;
  communication?: Communication;
  onSuccess?: (communicationId: string) => void;
  onCancel?: () => void;
};

/**
 * Stepped form for logging and editing patient outreach communications.
 * @agentic connections
 */
export const CommunicationForm = forwardRef<
  CommunicationFormRef,
  CommunicationFormProps
>(function CommunicationForm(
  {
    patientId: patientId,
    communication: communication,
    onSuccess,
    onCancel,
  } = {},
  ref,
) {
  const medplum = useMedplum();
  const communicationFacade = createCommunicationFacade(medplum);
  const navigate = useNavigate();
  const { patientId: urlPatientId } = useStableParams();

  // Use prop patientId if provided, otherwise fall back to URL
  const effectivePatientId = patientId || urlPatientId;
  const { data: patientResource } = usePatientResource(effectivePatientId);
  const patient = patientResource as Patient | undefined;
  const profile = useMedplumProfile() as Practitioner;
  const { flags: patientFlags } = usePatientFlags(effectivePatientId);

  // Load practitioner roles for follow-up task owner prefill
  const { data: practitionerRoleResources } = usePractitionerRoleOptions({
    organization: `Organization/${CONFIG.VITE_BENCHMARK_ORGANIZATION_ID}`,
    active: "true",
    _include: "PractitionerRole:practitioner",
    _count: PRACTITIONER_ROLE_PAGINATION_COUNT,
  });

  // Use prop communication if provided, otherwise fetch from URL
  const existingCommunication = communication;

  const [isLoading, setIsLoading] = useState(true);
  const [_originalFormData, setOriginalFormData] =
    useState<CommunicationFormData>({
      status: "completed", // Required field
    });

  // Form state management with Mantine
  const form = useForm<CommunicationFormData>({
    initialValues: {
      status: "completed",
      sent: "",
      "patient-engagement-status": "",
      "followup-flags": [],
      "followup-mode": undefined,
      "followup-activity-code": "",
      "followup-care-plan-id": "",
      "followup-task-type": "",
      "followup-due-date": "",
      "followup-description": "",
      "followup-assigned-to": "",
      "followup-assigned-to-display": "",
    },
    validate: {
      sent: (value) => (!value ? "Time is required" : null),
    },
  });

  const { createSubmissionHandler, isSubmitting } = useFormSubmission();

  // Get form protection for modal integration
  const { checkProtection, markAsClean, ConfirmationModal } = useFormProtection(
    form,
    {
      enabled: true,
      blockNavigation: true,
      confirmationMessage:
        "You have unsaved changes. Are you sure you want to close without saving?",
    },
  );

  // Expose form protection to parent component (modal) through ref
  useImperativeHandle(ref, () => {
    return {
      checkProtection,
    };
  }, [checkProtection]);

  // Prefill owner to current user's role when options load (only once)
  const ownerPrefilledRef = useRef(false);
  useEffect(() => {
    if (!practitionerRoleResources) return;
    if (ownerPrefilledRef.current) return;
    if (form.values["followup-assigned-to"]) {
      ownerPrefilledRef.current = true;
      return;
    }
    const roles = (practitionerRoleResources || []) as PractitionerRole[];
    const myRole = roles.find(
      (r) => r.practitioner?.reference === `Practitioner/${profile?.id}`,
    );
    if (myRole?.id) {
      form.setFieldValue("followup-assigned-to", myRole.id);
      form.setFieldValue(
        "followup-assigned-to-display",
        myRole.practitioner?.display || "",
      );
      ownerPrefilledRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practitionerRoleResources, profile?.id]);

  // Load existing communication data
  useEffect(() => {
    async function loadInitialData() {
      if (!patient) {
        return;
      }

      try {
        setIsLoading(true);

        if (!existingCommunication) {
          // No existing communication - create empty form data with current time
          const now = new Date().toISOString();
          const emptyData: CommunicationFormData = {
            sent: now,
          };

          // Get engagement status (production uses V1 system URL for all codes)
          const engagementTag = patient.meta?.tag?.find(
            (t) => t.system === PATIENT_TAG_MAPPINGS.engagementStatus.system,
          );
          if (engagementTag) {
            emptyData["patient-engagement-status"] = engagementTag.code;
          }

          form.setValues(emptyData);
          form.setInitialValues(emptyData);
          form.resetDirty();
          setOriginalFormData(emptyData);
        } else {
          // Existing communication - load form data
          const data = communicationToForm(existingCommunication);

          // Ensure follow-up fields are initialized
          const formDataWithDefaults: CommunicationFormData = {
            ...data,
            "followup-flags": data["followup-flags"] || [],
            "followup-mode": data["followup-mode"],
            "followup-activity-code": data["followup-activity-code"] || "",
            "followup-care-plan-id": data["followup-care-plan-id"] || "",
            "followup-task-type": data["followup-task-type"] || "",
            "followup-due-date": data["followup-due-date"] || "",
            "followup-description": data["followup-description"] || "",
            "followup-assigned-to": data["followup-assigned-to"] || "",
            "followup-assigned-to-display":
              data["followup-assigned-to-display"] || "",
          };

          form.setValues(formDataWithDefaults);
          form.setInitialValues(formDataWithDefaults);
          form.resetDirty();
          setOriginalFormData(formDataWithDefaults);
        }
      } catch (error) {
        console.error("Error loading communication data:", error);
        showErrorNotification("Failed to load communication data");
      } finally {
        setIsLoading(false);
      }
    }

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingCommunication, patient?.id, medplum]);

  // Form submission handler
  const handleSubmit = useCallback(
    async (formData: CommunicationFormData) => {
      if (!patient || !profile) {
        showErrorNotification("Missing patient or practitioner information");
        return;
      }

      const communication = await communicationFacade.upsert({
        formData,
        patient,
        practitioner: profile,
        existingCommunication: existingCommunication as
          Communication | undefined,
        onWarning: (msg) => showWarningNotification(msg),
      });

      showSuccessNotification(
        `Communication ${existingCommunication ? "updated" : "logged"} successfully.`,
      );

      // Mark form as clean after successful submission
      markAsClean();

      // Use callback if provided, otherwise navigate
      if (onSuccess && communication.id) {
        onSuccess(communication.id);
      } else {
        navigate(`/Patient/${patient.id}`)?.catch(console.error);
      }
    },
    [
      profile,
      patient,
      communicationFacade,
      navigate,
      existingCommunication,
      onSuccess,
      markAsClean,
    ],
  );

  // Define form steps
  const formSteps = [
    {
      id: "details",
      title: "Details",
      subtitle: "Enter outreach details",
      requiredFields: ["sent", "medium", "payload"],
    },
    {
      id: "follow-up",
      title: "Follow-Up",
      subtitle:
        "Create a follow-up task or update engagement status. (Optional)",
      optional: true,
      // Hide this step when editing existing communication
      showWhen: () => !existingCommunication,
      requiredFields: [
        // Plan activity mode: require activity and care plan
        ...(form.values["followup-mode"] === "plan-activity"
          ? ["followup-activity-code", "followup-care-plan-id"]
          : []),
        // Custom task mode: require description and owner
        ...(form.values["followup-mode"] === "custom"
          ? ["followup-description", "followup-assigned-to"]
          : []),
      ],
    },
  ];

  // Handle cancel - protection is handled by modal's onClose handler
  const handleCancel = useCallback(() => {
    if (!onCancel) return;
    onCancel();
  }, [onCancel]);

  // Show skeleton while loading existing communication data
  if (!patient || !profile || isLoading) {
    return <Skeleton height={300} />;
  }

  return (
    <>
      <SteppedForm
        form={form}
        steps={formSteps}
        onSubmit={createSubmissionHandler(handleSubmit)}
        isSubmitting={isSubmitting}
        submitText={existingCommunication ? "Update Outreach" : "Log Outreach"}
        onCancel={handleCancel}
      >
        {/* Step 1: Details */}
        <FormStep id="details">
          {/* Essential Communication Details */}
          <FormSection title="Details">
            <FormGroup>
              <FormField
                label="Time"
                required
                error={form.errors.sent as string}
              >
                <TextInput
                  type="datetime-local"
                  value={
                    form.values.sent
                      ? formatDateTimeForInput(form.values.sent)
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      const isoValue = formatInputForDateTime(value);
                      form.setFieldValue("sent", isoValue);
                    } else {
                      form.setFieldValue("sent", "");
                    }
                  }}
                  error={form.errors.sent}
                />
              </FormField>
              <FormField
                label="Method"
                required
                error={form.errors.medium as string}
              >
                <Select
                  placeholder="Select method"
                  value={
                    Array.isArray(form.values.medium) &&
                    form.values.medium[0]?.coding?.[0]?.code
                      ? form.values.medium[0].coding[0].code
                      : ""
                  }
                  onChange={(value) => {
                    if (value) {
                      const mediumOption =
                        COMMUNICATION_MEDIUM_SELECT_OPTIONS.find(
                          (option) => option.value === value,
                        );
                      if (mediumOption) {
                        form.setFieldValue("medium", [
                          {
                            coding: [
                              {
                                system:
                                  "http://terminology.hl7.org/CodeSystem/v3-ParticipationMode",
                                code: mediumOption.value,
                                display: mediumOption.label,
                              },
                            ],
                          },
                        ]);
                      }
                    } else {
                      form.setFieldValue("medium", undefined);
                    }
                  }}
                  data={COMMUNICATION_MEDIUM_SELECT_OPTIONS}
                  searchable
                  clearable
                  error={!!form.errors.medium}
                />
              </FormField>
            </FormGroup>
          </FormSection>

          {/* Communication Content */}
          <FormField
            label="Content"
            variant="section"
            showDivider={false}
            required
            description="Describe what was discussed or communicated"
            error={form.errors.payload as string}
          >
            <Textarea
              value={
                Array.isArray(form.values.payload) &&
                form.values.payload[0]?.contentString
                  ? form.values.payload[0].contentString
                  : ""
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  form.setFieldValue("payload", [{ contentString: value }]);
                } else {
                  form.setFieldValue("payload", undefined);
                }
              }}
              autosize
              minRows={3}
              placeholder="Describe what was discussed or communicated"
              error={form.errors.payload ? <></> : undefined}
            />
          </FormField>
        </FormStep>

        {/* Step 2: Follow-Up */}
        <FormStep id="follow-up">
          <PatientEngagementStatusSection
            form={form}
            required={false}
            showDivider
            description="Optionally update member's engagement status based on this communication"
          />

          {/* Optional: Add patient flags */}
          <FollowUpFlagsSection form={form} patientFlags={patientFlags} />

          {/* Optional Follow-Up Task Creation */}
          <FollowUpTaskSection
            form={form}
            patientId={effectivePatientId}
            practitionerRoleOptions={
              (practitionerRoleResources || []) as PractitionerRole[]
            }
          />
        </FormStep>
      </SteppedForm>

      <ConfirmationModal />
    </>
  );
});
